import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { showGoodbye } from "@/components/CuteLoader";

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Preview state lives server-side in the httponly token cookie; these are the only
// client-side keys any bundle (current or older deployed) may have used to hint preview.
// Purging them defensively guarantees no lingering preview hint survives login/logout.
const PREVIEW_KEYS = ["preview_employee_id", "preview_employee", "active_view", "previewEmployee"];
export function purgePreviewClientState() {
  try {
    for (const key of PREVIEW_KEYS) {
      window.localStorage?.removeItem(key);
      window.sessionStorage?.removeItem(key);
    }
  } catch (_) {
    /* storage may be unavailable; nothing to purge */
  }
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isCheckingAuth = useRef(false); // Prevent concurrent checkAuth calls

  // Memoize checkAuth so it has a stable reference
  const checkAuth = useCallback(async () => {
    // Prevent multiple concurrent calls
    if (isCheckingAuth.current) return;
    isCheckingAuth.current = true;

    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
      setLoading(false);
    } catch (error) {
      setUser(false);
      setLoading(false);
    } finally {
      isCheckingAuth.current = false;
    }
  }, []); // Empty deps - this function never changes

  // Initial auth check - only once on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Token refresh interval - only depends on whether user is logged in
  useEffect(() => {
    if (!user || user === false) return;
    
    const refreshInterval = setInterval(async () => {
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }, 11 * 60 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [user ? true : false]); // Only re-run when login status changes, not on user object changes

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      // New response format includes user object
      if (data.user) {
        setUser(data.user);
      } else {
        setUser(data); // Fallback for old format
      }

      // A successful login always lands in a clean, writable state — drop any
      // lingering preview hint from client storage (server token is already fresh).
      purgePreviewClientState();

      // Small delay to ensure cookies are set before any redirects
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return { success: true };
    } catch (e) {
      // Distinguish between server unreachable and bad credentials
      if (!e.response) {
        // Network error - server unreachable
        return { 
          success: false, 
          error: "The app is updating — try again in a few minutes",
          type: "network"
        };
      } else if (e.response.status === 401) {
        // Authentication error - bad credentials
        return { 
          success: false, 
          error: "Username or password incorrect",
          type: "auth"
        };
      } else {
        // Other error
        const message = formatApiErrorDetail(e.response?.data?.detail) || e.message;
        return { success: false, error: message };
      }
    }
  }, []);

  const logout = useCallback(async () => {
    showGoodbye();
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      // Server drops the token cookie (and with it any preview claim); also purge
      // client storage so logging out of a preview session lands on a clean login page.
      purgePreviewClientState();
      setUser(false);
    }
  }, []);

  const setPassword = useCallback(async (newPassword) => {
    try {
      await axios.post(
        `${API}/auth/set-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      await checkAuth();
      return { success: true };
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { success: false, error: message };
    }
  }, [checkAuth]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    setPassword,
    checkAuth
  }), [user, loading, login, logout, setPassword, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}